import {StockInformationModel} from "../../../models/domain/stockInformationModel";
import {Alert, Badge, Button, Card, Form, Modal} from "react-bootstrap";
import {AiOutlineAlert, AiOutlineMail} from "react-icons/ai";
import * as React from "react";
import {useState} from "react";
import Countdown, {CountdownRenderProps} from "react-countdown";
import {formatCountdown, validateEmail} from "../../../utils/helpers";
import {InTransitModel} from "../../../models/view/inTransitModel";
import {InStockModel} from "../../../models/view/inStockModel";
import {BuiltToOrderModel} from "../../../models/view/builtToOrderModel";
import {OutOfStockModel} from "../../../models/view/outOfStockModel";
import {useStockInformationQuery} from "../../../hooks/useStockInformationQuery";
import {FormProvider, useForm} from "react-hook-form";
import {TextArea} from "../../../components/textArea";
import {ValidationMessage} from "../../../components/validationMessage";
import {useMutation} from "@apollo/client";
import {contentService} from "../../../api/contentService";
import {FaShippingFast} from "react-icons/fa";
import {useAppContext} from "../../../hooks/useAppContext";
import {BackOrderModel} from "../../../models/view/BackOrderModel";
import { BsCheck2Square } from "react-icons/bs";

export const StockInformationContainer = () => {
    const {model, error} = useStockInformationQuery();
    if (!model || error) return <></>;
    return (
        <Card body className="my-3" id="stock-information">
            <StockInformation model={model}/>
        </Card>
    );
};

const StockInformation = ({
                              model: {
                                  isDiscontinued,
                                  isInStock,
                                  isBuiltToOrder,
                                  isBackOrderable,
                                  isInTransit,
                                  isInStockModel,
                                  IsBuiltToOrderModel,
                                  isBackOrderModel,
                                  isOutOfStockModel,
                                  isInTransitModel
                              }
                          }: { model: StockInformationModel }) => {
    if (isDiscontinued)
        return <Discontinued/>;

    if (isInStock)
        return isBuiltToOrder ? <BuiltToOrder model={IsBuiltToOrderModel}/> : <InStock model={isInStockModel}/>;

    if (isInTransit)
        return <InTransit model={isInTransitModel}/>;

    if (isBackOrderable)
        return <BackOrder model={isBackOrderModel}/>

    return <OutOfStock model={isOutOfStockModel}/>;
}

const Discontinued = () => {
    return (
        <>
            <div className="text-danger fs-5 fw-bold">Discontinued</div>
            <div className="fs-5">We're sorry, but this product is discontinued and is no
                longer available for purchase.
            </div>
        </>
    )
}

const BuiltToOrder = ({model: {showSubtitle, subtitle}}: { model: BuiltToOrderModel }) => {
    return (
        <>
            <div className="text-dark fs-5 fw-bold">Built-To-Order</div>
            {showSubtitle && <div>{subtitle}</div>}
        </>
    )
}

const InStock = ({
                     model: {
                         showInStock,
                         showLowStockWarning,
                         showShipsInMessage,
                         lowStockWarningMessage,
                         leadIn,
                         shipsSameDay,
                         canShipToday,
                         shipCutoffDate,
                         formatted: {
                             shipEstimatedDateUtc
                         }
                     }
                 }: { model: InStockModel }) => {
    const shippingCountDownRenderer = (countdown: CountdownRenderProps) => {
        return (
            <ShippingCountdown
                shipEstimatedDateUtc={shipEstimatedDateUtc}
                days={countdown.days}
                seconds={countdown.seconds}
                minutes={countdown.minutes}
                hours={countdown.hours}
                completed={countdown.completed}
                canShipToday={canShipToday}
            />
        )
    }

    return (
        <>
            {showInStock &&
                <div className="text-dark fs-5 fw-bold d-flex align-items-center justify-content-between flex-wrap">IN
                    STOCK
                    {(shipsSameDay || leadIn === 1) && (
                        <Badge bg="success" className="d-flex align-items-center position-absolute top-0 end-0" style={{borderRadius:"0",
                            borderBottomLeftRadius: "12px"}}>
                            <FaShippingFast aria-hidden size={20} className="me-1"/>
                            {shipsSameDay ? 'Ships Same Day!' : 'Fast Shipping!'}
                        </Badge>
                    )}
                </div>}
            {shipsSameDay && (
                <div>
                    <div>{shipsSameDay && (
                        /* Changes to this key restart the countdown,
                        so when shipCutoffDate is refreshed, the countdown will refresh. */
                        <Countdown key={shipCutoffDate.toString()}
                                   date={shipCutoffDate}
                                   intervalDelay={60000}
                                   renderer={shippingCountDownRenderer}/>)}
                    </div>
                </div>
            )}
            {showShipsInMessage && (<div className="d-flex align-items-center">Ships in <strong
                className="text-success ms-1">{leadIn} business day{leadIn > 1 ? "s" : ""}</strong>.</div>)}
            {showLowStockWarning && (
                <Alert variant="warning"
                       className="mt-2 fs-5 p-2 d-flex align-items-center fade stock-low-warning mb-0">
                    <AiOutlineAlert size={25} className="me-1 mb-1"/>
                    <div>{lowStockWarningMessage}</div>
                </Alert>
            )}
        </>
    )
}

const ShippingCountdown = ({canShipToday, shipEstimatedDateUtc, seconds, minutes, hours, days, completed}: {
    canShipToday: boolean
    shipEstimatedDateUtc: string
    completed: boolean
    days: number
    hours: number
    minutes: number
    seconds: number
}) => {
    if (completed) return <></>

    const formattedShipCutOffDate = formatCountdown({
        days,
        hours,
        minutes,
        seconds,
    })

    return (
        <div className="mt-2">
            {canShipToday ? (
                <>
                    Ships<strong className="mx-1 text-success">TODAY</strong>if ordered within the next{" "}
                    <strong className="ms-1 text-success">{formattedShipCutOffDate}</strong>.
                </>
            ) : (
                <>
                    Ships on<strong className="mx-1 text-success">{shipEstimatedDateUtc}</strong>if ordered
                    within the next<strong className="ms-1 text-success">{formattedShipCutOffDate}</strong>.
                </>
            )}
        </div>
    )
}

const InTransit = ({model: {title, subtitle}}: { model: InTransitModel }) => {
    return (
        <>
            <div className="text-dark fs-5 fw-bold">{title}</div>
            <div>
                <div><i className="fa fa-info-circle"></i>{subtitle})</div>
            </div>
        </>
    )
}

const BackOrder = ({model: {leadOut, showMoreExpectedMessage}}: {
    model: BackOrderModel
}) => {
    return (
        <>
            <div className="fs-5 fw-bold">Available for Backorder</div>
            {showMoreExpectedMessage && (
                <div className="fw-bold">More expected in approximately {leadOut} day{leadOut > 1 ? "s" : ""}.</div>
            )}
        </>
    )
}

const OutOfStock = ({model: {showNotifyMeWhenInStock}}: {
    model: OutOfStockModel
}) => {
    return (
        <>
            <div className="text-danger fs-5 fw-bold">OUT OF STOCK</div>
            <div className="position-relative">
                This product is temporarily out of stock and is currently unavailable
                for purchase. Please check back regularly as we anticipate new stock in
                the future.
            </div>
            <div>
                {showNotifyMeWhenInStock && <NotifyMeWhenInStockModal/>}
            </div>
        </>
    )
}

const NotifyMeWhenInStockModal = () => {
    const globalErrorKey = "root.serverError";
    const emailFieldKey = "email";

    const [formState, setFormState] = useState({show: false, success: false})

    const showModal = () => setFormState({...formState, show: true});

    const hideModal = () => {
        setFormState({...formState, show: false, success: false})
        resetForm();
    };

    type FormValues = { email: string };

    const form = useForm<FormValues>({
        mode: 'onBlur', // Trigger validations on blur
        reValidateMode: "onBlur" // Enable revalidation on error after form submission
    });

    const {
        handleSubmit,
        setValue,
        trigger,
        reset,
        setError,
        clearErrors,
        formState: {isSubmitting, isValid}
    } = form;

    function resetForm() {
        // Clear out any form values
        setValue(emailFieldKey, "")
        // Reset validation state of the form
        reset()
    }

    const {productId, catalogId} = useAppContext();

    const [createNotifyProductAsync] = useMutation(contentService.notifyProduct.create, {
        errorPolicy: "all"
    });

    const submitForm = handleSubmit(async ({email}) => {
        await createNotifyProductAsync({
            variables: {
                email: email,
                productId: productId,
                catalogId: catalogId
            }
        }).then(response => {
            if (response.errors) {
                handleError("An error has occurred. Please try again.");
                return;
            } else {
                setFormState({...formState, success: true})
            }
        })
    });
    const handleError = (errorMessage: string) => {
        // Re-enable the submit button for a retry
        // noinspection JSIgnoredPromiseFromCall
        trigger(emailFieldKey)
        // Set a global error to display to the user
        setError(globalErrorKey, {type: "500", message: errorMessage})
    }

    const clearError = () => {
        // Clear the global error
        clearErrors(globalErrorKey);
    }

    return (<>
            <Button variant="danger" className="mt-2 btn-notify" onClick={showModal}>
                <AiOutlineMail className="me-1" aria-hidden="true"/> Notify me when it's in stock
            </Button>
            <Modal show={formState.show} onHide={hideModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>We'll notify you when this product is back in stock</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <FormProvider {...form}>
                        <Form>
                            {formState.success ? <div className="alert alert-success">
                                <BsCheck2Square className="me-1" />
                                Thank you! We will send you an email when this product is back in stock.
                            </div> : <TextArea
                                className={"mb-3"}
                                type={"email"}
                                fieldName={"email"}
                                displayName={"Please Enter Your Email Address"}
                                placeholder={"Enter Email Address"}
                                rows={1}
                                validation={{
                                    enabled: true,
                                    rules: {validateEmail: (email: string) => validateEmail(email) ? true : "The Email field is not a valid e-email address."}
                                }}
                                onChange={clearError}
                                maxLength={250}
                                required={true}/>}
                            <ValidationMessage fieldName={globalErrorKey}/>
                        </Form>
                    </FormProvider>
                </Modal.Body>
                <Modal.Footer>
                    {formState.success ? <Button variant="secondary" onClick={hideModal}>Close</Button> : (
                        <>
                            <Button variant="secondary"
                                    onClick={hideModal}>Cancel</Button>
                            <Button variant="primary"
                                    disabled={isSubmitting || !isValid}
                                    onClick={submitForm}>Submit</Button>
                        </>
                    )}
                </Modal.Footer>
            </Modal>
        </>
    )
}